import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';

import { outputIfNoVariables, getFirst } from '../../../utilities/misc';
import { rem } from '../../../utilities/style';
import { colors } from '../../../theme/colors';
import { media } from '../../../theme/media';
import { containerStyles } from '../../../styles/layout';
import { headers } from '../../../styles/type';

import ArticleCard from '../../ArticleCard';
import Button from '../../buttons/Button';
import FeaturedResourcesLinks from './FeaturedResourcesLinks';
import TextRenderer from '../../TextRenderer';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};
  padding: ${rem(32)} 0;

  ${media('>tiny')} {
    padding: ${rem(40)} 0 ${rem(56)};
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Header = styled.div`
  margin-bottom: ${rem(40)};
`;

const Headline = styled.h2`
  ${headers.h3}
  color: ${colors.blue['800']};
`;

const Grid = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  gap: ${rem(20)};
  grid-template-areas:
  'featured'
  'links';

  ${media('>tiny')} {
    grid-template-areas:
    'links featured';
  }

  ${media('>smedium')} {
    grid-template-areas:
    'links featured featured featured';
    gap: ${rem(30)};
  }

  ${media('>large')} {
    gap: ${rem(40)};
  }
`;

const FeaturedLinksContainer = styled.div`
  grid-area: links;
`;

const FeaturedResourceContainer = styled.div`
  grid-area: featured;
`;

const ButtonContainer = styled.div`
  margin-top: ${rem(40)};
  text-align: center;
`;

const FeaturedResources = ({
  headline,
  featuredResource,
  resourceLinks,
  relatedResources,
  ctaText,
  ctaLink,
  backgroundColor
}) => {
  const hasFeatured = featuredResource !== undefined;
  const useFirstItemAsFeatured = !hasFeatured && resourceLinks !== null;
  const offset = useFirstItemAsFeatured ? 1 : 0;
  const featuredItem = !useFirstItemAsFeatured ? featuredResource : relatedResources[0];

  return (
    <Container
      backgroundColor={backgroundColor}
    >
      <ContentWrapper>
        <Header>
          <Headline>
            <TextRenderer text={headline} />
          </Headline>
        </Header>
        <Grid>
          {resourceLinks && (
            <FeaturedLinksContainer>
              <FeaturedResourcesLinks links={resourceLinks} />
            </FeaturedLinksContainer>
          )}
          {(hasFeatured || useFirstItemAsFeatured) && featuredItem && (
            <FeaturedResourceContainer>
              <ArticleCard
                category={featuredItem.resourceType?.title}
                title={getFirst(featuredItem.page)?.title}
                link={featuredItem.externalUrl || `/resources/${getFirst(featuredItem.page)?.slug}`}
                image={featuredItem.image?.largeImage}
                imageAlt={featuredItem.image?.description}
                displayRow
                featured={hasFeatured}
              />
            </FeaturedResourceContainer>
          )}
          {relatedResources.slice(0 + offset, 4 + offset).map((resource) => (
            <ArticleCard
              key={resource.id}
              category={resource.resourceType?.title}
              title={getFirst(resource.page)?.title}
              link={resource.externalUrl || `/resources/${getFirst(resource.page)?.slug}`}
              image={resource.image?.smallImage}
              imageAlt={resource.image?.description}
            />
          ))}
        </Grid>
        {ctaLink && ctaText && (
          <ButtonContainer>
            <Button
              to={ctaLink}
              aria-label={outputIfNoVariables(`${headline} — ${ctaText}`)}
            >
              <TextRenderer text={ctaText} />
            </Button>
          </ButtonContainer>
        )}
      </ContentWrapper>
    </Container>
  );
};

FeaturedResources.propTypes = {
  headline: PropTypes.string,
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  featuredResource: PropTypes.shape({
    id: PropTypes.string.isRequired,
    page: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        slug: PropTypes.string
      })
    ),
    image: PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      largeImage: PropTypes.object,
      // eslint-disable-next-line react/forbid-prop-types
      smallImage: PropTypes.object,
      description: PropTypes.string
    }),
    externalUrl: PropTypes.string,
    resourceType: PropTypes.shape({
      title: PropTypes.string,
    }),
  }),
  resourceLinks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired
  })),
  backgroundColor: PropTypes.oneOf(['Blue', 'White'])
};

export const query = graphql`
  fragment FeaturedResourceCard on ContentfulResource {
    id
    page {
      title
      slug
    }
    image {
      largeImage: gatsbyImageData(
        width: 450
        height: 300
        quality: 75
        resizingBehavior: FILL
      )
      smallImage: gatsbyImageData(
        width: 300
        height: 200
        quality: 75
        resizingBehavior: FILL
      )
      description
    }
    externalUrl
    resourceType {
      title
    }
  }

  fragment FeaturedResourcesFragment on ContentfulFeaturedResources {
    id
    __typename
    headline
    ctaText
    ctaLink
    featuredResource {
      ...FeaturedResourceCard
    }
    resourceLinks {
      id
      headline
      link
    }
    relatedResources {
      ...FeaturedResourceCard
    }
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default FeaturedResources;
